import { FormFields } from "./interfaces";
import GoogleMapsUtility from "~/utils/GoogleMapsUtility";

/**
 * Gestisce l'autocomplete di Google Maps per la ricerca
 */
export class GoogleMapsAutocomplete {
    private userHasSelectedPlace: boolean = false;

    /**
     * Inizializza il servizio di autocomplete di Google Maps
     */
    public init(type: string): void {
        const input = document.getElementById(`autocomplete-${type}`) as HTMLInputElement;
        if (!input) return;

        const autocompleteService = new window.google.maps.places.AutocompleteService();
        let debounceTimeout: number;
        let selectedIndex = -1;
        let selectedValue = '';

        const formattedAddress = sessionStorage.getItem('formattedAddress');
        if (formattedAddress) {
            input.value = formattedAddress;
        }

        input.addEventListener('input', () => {
            const query = input.value.trim().toLowerCase();
            const resultsContainer = document.getElementById(`results-${type}`);
            if (resultsContainer) {
                resultsContainer.style.display = 'block';
            }

            clearTimeout(debounceTimeout);
            debounceTimeout = window.setTimeout(() => this.handleInput(query, type, autocompleteService), 300);
        });

        input.addEventListener('keydown', (event) => {
            const resultsContainer = document.getElementById(`results-${type}`);
            if (!resultsContainer) return;

            const results = resultsContainer.getElementsByTagName('li');
            if (results.length === 0) return;

            switch (event.key) {
                case 'ArrowDown':
                    selectedIndex = (selectedIndex + 1) % results.length;
                    this.highlightResult(results, selectedIndex);
                    selectedValue = results[selectedIndex].innerText;
                    break;
                case 'ArrowUp':
                    selectedIndex = (selectedIndex - 1 + results.length) % results.length;
                    this.highlightResult(results, selectedIndex);
                    selectedValue = results[selectedIndex].innerText;
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (selectedIndex >= 0) {
                        input.value = selectedValue;
                        (results[selectedIndex] as HTMLElement).click();
                    }
                    break;
                case 'Escape':
                    resultsContainer.style.display = 'none';
                    resultsContainer.classList.remove('results-open');
                    break;
            }
        });
    }

    /**
     * Restituisce se l'utente ha selezionato un luogo
     */
    public hasUserSelectedPlace(): boolean {
        return this.userHasSelectedPlace;
    }

    /**
     * Resetta il flag di selezione
     */
    public resetUserSelection(): void {
        this.userHasSelectedPlace = false;
    }

    // ====== METODI PRIVATI ======

    private highlightResult(results: HTMLCollectionOf<HTMLLIElement>, index: number): void {
        for (let i = 0; i < results.length; i++) {
            results[i].classList.remove('highlight');
        }
        if (index >= 0 && results[index]) {
            results[index].classList.add('highlight');
        }
    }

    private async handleInput(query: string, type: string, autocompleteService: any): Promise<void> {
        const resultsContainer = document.getElementById(`results-${type}`);
        if (!resultsContainer) return;

        const isNumeric = /^\d+$/.test(query);

        if (isNumeric && query.length !== 5) {
            resultsContainer.innerHTML = '';
            return;
        }

        if (GoogleMapsUtility.isValidSearchQuery(query) || GoogleMapsUtility.isPossibleItalianCAP(query)) {
            const cachedResults = localStorage.getItem(query);
            if (cachedResults) {
                this.displayResults(JSON.parse(cachedResults), type);
            } else {
                try {
                    const predictions = await GoogleMapsUtility.getPlacePredictions(query, autocompleteService);
                    localStorage.setItem(query, JSON.stringify(predictions));
                    this.displayResults(predictions, type);
                } catch (error) {
                    this.userHasSelectedPlace = false;
                    console.error('Error:', error);
                    localStorage.setItem(query, JSON.stringify([]));
                    resultsContainer.innerHTML = '';
                }
            }
        } else {
            resultsContainer.innerHTML = '';
        }
    }

    private displayResults(predictions: any[], type: string): void {
        const normalizedPredictions = GoogleMapsUtility.normalizePredictions(predictions);

        const results = normalizedPredictions.map((prediction: any) => {
            const li = document.createElement('li');
            li.innerHTML = `<strong>${GoogleMapsUtility.getAddressType(prediction.type)}</strong>${prediction.label}`;
            li.addEventListener('click', () => this.handleSelect(prediction.value, type));
            return li;
        });

        const resultsContainer = document.getElementById(`results-${type}`);
        if (!resultsContainer) return;

        resultsContainer.innerHTML = '';
        results.forEach((result: HTMLLIElement) => resultsContainer.appendChild(result));

        if (results.length > 0) {
            resultsContainer.classList.add('results-open');
        } else {
            resultsContainer.classList.remove('results-open');
        }
    }

    private async handleSelect(placeId: string, type: string): Promise<void> {
        this.userHasSelectedPlace = true;

        const placeService = new window.google.maps.places.PlacesService(document.createElement('div'));

        try {
            const place = await GoogleMapsUtility.getPlaceDetails(placeId, placeService);
            const input = document.getElementById(`autocomplete-${type}`) as HTMLInputElement;
            if (input) {
                input.value = place.formatted_address;
            }

            this.updateHiddenFields(place);

            const resultsContainer = document.getElementById(`results-${type}`);
            if (resultsContainer) {
                resultsContainer.style.display = 'none';
                resultsContainer.classList.remove('results-open');
            }
        } catch (error) {
            console.error("Errore:", error);
        }
    }

    private updateHiddenFields(place: any): void {
        const form = document.getElementById('frmcerca');
        if (!form) return;

        const fields: FormFields = {
            indirizzo: form.querySelector('[name="Indirizzo"]'),
            provincia: form.querySelector('[name="Provincia"]'),
            comune: form.querySelector('[name="Comune"]'),
            regione: form.querySelector('[name="Regione"]'),
            latitudineNW: form.querySelector('[name="LatitudineNW"]'),
            latitudineSE: form.querySelector('[name="LatitudineSE"]'),
            longitudineNW: form.querySelector('[name="LongitudineNW"]'),
            longitudineSE: form.querySelector('[name="LongitudineSE"]')
        };

        const replaceDotWithComma = (value: string) => value.replace('.', ',');

        if (GoogleMapsUtility.isAddressType(place)) {
            this.updateAddressFields(fields, place, replaceDotWithComma);
        } else {
            this.updateLocationFields(fields, place);
        }
    }

    private updateAddressFields(fields: FormFields, place: any, replaceDotWithComma: (value: string) => string): void {
        if (fields.indirizzo) {
            fields.indirizzo.value = place.formatted_address;
        }

        if (fields.latitudineNW && fields.latitudineSE && fields.longitudineNW && fields.longitudineSE) {
            const bounds = place.geometry.viewport;
            fields.latitudineNW.value = replaceDotWithComma(bounds.getNorthEast().lat().toString());
            fields.longitudineNW.value = replaceDotWithComma(bounds.getNorthEast().lng().toString());
            fields.latitudineSE.value = replaceDotWithComma(bounds.getSouthWest().lat().toString());
            fields.longitudineSE.value = replaceDotWithComma(bounds.getSouthWest().lng().toString());

            if (fields.provincia) fields.provincia.value = '';
            if (fields.comune) fields.comune.value = '';
            if (fields.regione) fields.regione.value = '';
        }
    }

    private updateLocationFields(fields: FormFields, place: any): void {
        if (fields.provincia) {
            fields.provincia.value = GoogleMapsUtility.normalizeProvincia(GoogleMapsUtility.getComponent(place.address_components, 'administrative_area_level_2'));
        }
        if (fields.comune) {
            fields.comune.value = GoogleMapsUtility.normalizeComune(GoogleMapsUtility.getComponent(place.address_components, 'locality'));
        }
        if (fields.regione) {
            fields.regione.value = GoogleMapsUtility.getComponent(place.address_components, 'administrative_area_level_1');
        }

        if (fields.latitudineNW && fields.latitudineSE && fields.longitudineNW && fields.longitudineSE) {
            fields.latitudineNW.value = '';
            fields.latitudineSE.value = '';
            fields.longitudineNW.value = '';
            fields.longitudineSE.value = '';
        }
    }
}
